import React from 'react';

const CHALLENGES = [
  'No centralized system to track customers, orders, or spending patterns',
  'Staff manually logging data across disconnected spreadsheets',
  'High overhead costs from duplicated effort and human error',
  'No visibility into which menu items, promotions, or customers drove the most revenue',
];

const SOLUTION_FEATURES = [
  { label: 'Customer Database', desc: 'Centralized profiles tracking visit history, preferences, and lifetime value' },
  { label: 'Automated Follow-ups', desc: 'Smart re-engagement messages triggered by visit frequency and spend' },
  { label: 'Cost Tracking Dashboard', desc: 'Real-time view of operational costs, waste, and supplier spending' },
  { label: 'AI-Powered Insights', desc: 'Pattern detection that surfaces which items, times, and promotions drive profit' },
];

const CaseStudy: React.FC = () => {
  return (
    <section id="case-study" className="py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section header */}
        <div className="max-w-2xl mb-14">
          <p className="text-blue-700 text-sm font-semibold uppercase tracking-widest mb-3">
            Client Story
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
            How We're Helping a Restaurant Take Control of Their Costs
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            A restaurant client came to us with a real problem: high operational costs and no system to understand where the money was going. Here's what we're building for them.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 mb-14">

          {/* The Challenge */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center text-red-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">The Challenge</p>
                <h3 className="text-lg font-bold text-gray-900">Costs were high. Nobody knew why.</h3>
              </div>
            </div>
            <ul className="space-y-3">
              {CHALLENGES.map((c, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{c}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* The Solution */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Our Solution</p>
                <h3 className="text-lg font-bold text-gray-900">A custom CRM built for their operations</h3>
              </div>
            </div>
            <div className="space-y-4">
              {SOLUTION_FEATURES.map((f) => (
                <div key={f.label} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{f.label}</p>
                    <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tech used */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-10">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">Built With</p>
          <div className="flex flex-wrap gap-2">
            {['React', 'TypeScript', 'Node.js', 'PostgreSQL', 'AWS', 'Claude AI', 'CI/CD Pipeline', 'Docker'].map((t) => (
              <span key={t} className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100">
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Status banner + CTA */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-800 rounded-2xl p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-blue-200 text-sm font-semibold uppercase tracking-widest">Founding Client Project</span>
            </div>
            <h3 className="text-xl font-bold mb-1">Similar problem in your business?</h3>
            <p className="text-blue-100 text-sm leading-relaxed max-w-lg">
              If your business is dealing with high operational costs and no clear system to track them, we can build something tailored to exactly how you work.
            </p>
          </div>
          <a
            href="#contact"
            className="shrink-0 bg-white text-blue-700 hover:bg-blue-50 font-semibold px-7 py-3 rounded-lg transition-colors duration-200 text-sm whitespace-nowrap"
          >
            Talk to Us
          </a>
        </div>

      </div>
    </section>
  );
};

export default CaseStudy;
